import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getAccountState,
  logoutSingleUser,
  logoutAllUsers,
  toggleInCreatorMode,
  accountActions,
} from "../../../redux/slices/accountSlice";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "../../../hooks/useNavigate";
import ProfilePic from "../../ProfilePic";
import { NavigationId } from "../../../navigation/AppParamList";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { MixpanelContext } from "../../../context/AnalyticsService";
import { DataLoaders } from "../../../redux/slices/dataSlice";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import {
  AccountData,
  NotificationType,
  TeamMemberRole,
  ThemeType,
} from "@markit/common.types";
import { AnimatePresence, domAnimation, LazyMotion, m } from "framer-motion";
import { Icon } from "@iconify/react";
import { DarkTheme, useTheme } from "../../../hooks/useTheme";
import { Colors } from "../../../utils/colors";
import RequestAccessModal from "../../DisplayModal/RequestAccessModal";
import MiniProfileItem from "../../DisplayItem/ProfileItem/MiniProfileItem";
import { useMultiAccountManager } from "../../../hooks/useMultiAccountManager";
import ProfileSwitchAccountModal from "./ProfileSwitchAccountModal";

type ProfileHeaderDropdownProps = {
  children: ReactNode;
  placement: "bottom" | "left" | "right";
  eventTheme?: ThemeType;
  nonCreatorPage?: boolean;
};

const ProfileHeaderDropdown = (props: ProfileHeaderDropdownProps) => {
  const { children, placement, eventTheme, nonCreatorPage } = props;
  const navigate = useNavigate();
  const { account } = useSelector(getAccountState);
  const {
    accountData,
    teamMemberData,
    additionalAccountData,
    originalAccountAdmin,
  } = account;
  const mixpanel = useContext(MixpanelContext);
  const dispatch = useDispatch();
  const { theme } = useTheme(eventTheme);
  const ref = useRef<any>(null);
  const ref2 = useRef<any>(null);

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [signOutDropdownVisible, setSignOutDropdownVisible] = useState(false);
  const [switchCreatorAccountVisible, setSwitchCreatorAccountVisible] =
    useState(false);
  const [requestAccessModal, setRequestAccessModal] = useState(false);

  const { switchToAdditionalAccount } = useMultiAccountManager();

  const isDarkTheme = useMemo(() => theme && theme === DarkTheme, [theme]);

  const styles = {
    leftPlacement: {
      left: "100%",
      top: 14,
    },
    bottomPlacement: {
      right: 14,
    },
    rightPlacement: {},
  };

  const allAccounts = useMemo(() => {
    return [accountData, ...additionalAccountData];
  }, [accountData, additionalAccountData]);

  useEffect(() => {
    const handler = (event: any) => {
      if (
        dropdownVisible &&
        ref.current &&
        !ref.current.contains(event.target)
      ) {
        setDropdownVisible(false);
        setSignOutDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [dropdownVisible]);

  useEffect(() => {
    const handler = (event: any) => {
      if (
        signOutDropdownVisible &&
        ref2.current &&
        !ref2.current.contains(event.target)
      ) {
        setSignOutDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [dropdownVisible, signOutDropdownVisible]);

  const viewProfile = useCallback(() => {
    setDropdownVisible(false);
    setSignOutDropdownVisible(false);
    if (accountData.inCreatorMode) {
      navigate(NavigationId.HOME_PROFILE);
    } else {
      dispatch(DataLoaders.user(accountData.uid));
      navigate(`/u/${accountData.username}`, {
        state: { username: accountData.username },
      });
    }
  }, [
    accountData.inCreatorMode,
    accountData.uid,
    accountData.username,
    dispatch,
    navigate,
  ]);

  const switchMode = useCallback(() => {
    // If you are email account and switching to creator mode, immediate switch.
    // Or if you are phone number account and switching to subscriber mode, immediate switch.
    if (
      (accountData.email && !accountData.inCreatorMode) ||
      (accountData.phoneNumber && accountData.inCreatorMode)
    ) {
      const currAccountMode = accountData.inCreatorMode;
      dispatch(toggleInCreatorMode(accountData.uid, accountData.inCreatorMode));
      showNotificationBanner(
        dispatch,
        "Switched",
        NotificationType.AFFIRMATIVE
      );
      if (!currAccountMode) {
        navigate("/home");
      } else {
        dispatch(DataLoaders.user(accountData.uid));
        navigate(`/u/${accountData.username}`, {
          state: { username: accountData.username },
        });
      }
    } else {
      setSwitchCreatorAccountVisible(true);
      setDropdownVisible(false);
    }
  }, [
    accountData.email,
    accountData.inCreatorMode,
    accountData.phoneNumber,
    accountData.uid,
    accountData.username,
    dispatch,
    navigate,
  ]);

  const signOut = useCallback(
    async (account: AccountData) => {
      mixpanel.track("Webapp: User Logout", {
        distinct_id: account.uid,
      });
      if (account.uid === accountData.uid && additionalAccountData.length > 0) {
        // If log out of current account, switch to additional account and then remove current account from redux
        await switchToAdditionalAccount(additionalAccountData[0].uid);
      }
      dispatch(logoutSingleUser(account.uid));
      setSignOutDropdownVisible(false);
    },
    [
      mixpanel,
      accountData.uid,
      additionalAccountData,
      dispatch,
      switchToAdditionalAccount,
    ]
  );

  const signOutAllAccounts = useCallback(() => {
    dispatch(logoutAllUsers());
    navigate("/");
  }, [dispatch, navigate]);

  const addAccountOnPress = useCallback(() => {
    if (accountData.isAdmin && originalAccountAdmin) {
      alert(
        "Switch off of the admin mode viewing account before adding a new account."
      );
      return;
    }
    dispatch(accountActions.setRedirectPath(location.pathname));
    navigate(NavigationId.WELCOME + "?addAccount=true");
  }, [accountData.isAdmin, dispatch, originalAccountAdmin, navigate]);

  const switchAccounts = useCallback(
    async (accountUid: string) => {
      const isPhoneAccount = additionalAccountData.some(
        (account) => account.uid === accountUid && account.phoneNumber
      );
      await switchToAdditionalAccount(
        accountUid,
        nonCreatorPage === false && isPhoneAccount
      );
      showNotificationBanner(
        dispatch,
        "Switched Account",
        NotificationType.AFFIRMATIVE
      );
      setDropdownVisible(false);
      setSignOutDropdownVisible(false);
    },
    [additionalAccountData, dispatch, nonCreatorPage, switchToAdditionalAccount]
  );

  const checkRequestAccess = useCallback(
    (callback: () => void) => {
      return () => {
        if (
          teamMemberData &&
          teamMemberData.role !== TeamMemberRole.FULL_ACCESS
        ) {
          setRequestAccessModal(true);
          return;
        }
        callback();
      };
    },
    [teamMemberData]
  );

  const renderItem = useCallback(
    (
      label: string,
      iconName: string,
      onPress: () => void,
      isLastItem?: boolean
    ) => (
      <m.div
        className="AlignedRowSpacedSelect"
        style={{
          paddingInline: 10,
          paddingBlock: 12,
          borderBottomRightRadius: isLastItem ? 12 : 0,
          borderBottomLeftRadius: isLastItem ? 12 : 0,
          backgroundColor: isDarkTheme ? Colors.GRAY10 : Colors.WHITE,
        }}
        whileHover={{
          backgroundColor: isDarkTheme ? Colors.GRAY7 : Colors.GRAY6,
        }}
        transition={{ duration: 0.2 }}
        onClick={onPress}
      >
        <span style={{ fontSize: 13, fontWeight: 500, ...theme.PrimaryText }}>
          {label}
        </span>
        <Icon icon={iconName} height={18} style={{ ...theme.PrimaryText }} />
      </m.div>
    ),
    [isDarkTheme, theme.PrimaryText]
  );

  const renderMiniProfileItem = useCallback(
    (account: AccountData, isSignOut?: boolean) => (
      <m.div
        key={account.uid}
        style={{
          paddingBlock: 5,
          paddingInline: 10,
          borderRadius: 7,
          backgroundColor: isDarkTheme ? Colors.GRAY10 : Colors.WHITE,
          pointerEvents:
            !isSignOut && account.uid === accountData.uid ? "none" : "all",
        }}
        whileHover={{
          backgroundColor: isDarkTheme ? Colors.GRAY7 : Colors.GRAY6,
        }}
      >
        <MiniProfileItem
          item={account}
          onItemPress={() =>
            isSignOut ? signOut(account) : switchAccounts(account.uid)
          }
          rightIcon={
            !isSignOut && account.uid === accountData.uid ? (
              <Icon
                icon="ion:checkmark"
                height={18}
                style={{ color: isDarkTheme ? Colors.WHITE : Colors.BLACK }}
              />
            ) : undefined
          }
          themeType={eventTheme || ThemeType.Light}
          hideModeLabel={isSignOut}
        />
      </m.div>
    ),
    [accountData.uid, eventTheme, isDarkTheme, signOut, switchAccounts]
  );

  return (
    <div>
      <div
        style={{ pointerEvents: dropdownVisible ? "none" : "all" }}
        onClick={() => setDropdownVisible(true)}
      >
        {children}
      </div>
      {requestAccessModal ? (
        <RequestAccessModal
          isVisible={requestAccessModal}
          setIsVisible={setRequestAccessModal}
        />
      ) : null}
      {switchCreatorAccountVisible ? (
        <ProfileSwitchAccountModal
          isVisible={switchCreatorAccountVisible}
          setIsVisible={setSwitchCreatorAccountVisible}
        />
      ) : null}
      <div ref={ref}>
        {dropdownVisible ? (
          <AnimatePresence>
            <LazyMotion features={domAnimation}>
              <m.div
                key="profilePicDropdown"
                className="HeaderProfilePicDropdown"
                style={{
                  padding: 4,
                  backgroundColor: isDarkTheme ? Colors.GRAY10 : Colors.WHITE,
                  border: `1px solid ${theme.DividerColor.borderColor}`,
                  ...(placement === "left"
                    ? styles.leftPlacement
                    : placement === "bottom"
                      ? styles.bottomPlacement
                      : styles.rightPlacement),
                }}
                initial={{
                  x: placement === "left" ? "-10px" : undefined,
                  y: placement === "bottom" ? "-10px" : undefined,
                  opacity: 0,
                  filter: "blur(2px)",
                }}
                animate={{
                  x: placement === "left" ? "14px" : undefined,
                  y: placement === "bottom" ? "14px" : undefined,
                  opacity: 1,
                  filter: "blur(0px)",
                }}
                exit={{
                  x: placement === "left" ? "-10px" : undefined,
                  y: placement === "bottom" ? "-10px" : undefined,
                  opacity: 0,
                  filter: "blur(2px)",
                }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <m.div
                  className="AlignedRowSpacedSelect"
                  style={{
                    padding: 7,
                    backgroundColor: isDarkTheme ? Colors.GRAY10 : Colors.WHITE,
                    borderTopRightRadius: 12,
                    borderTopLeftRadius: 12,
                  }}
                  whileHover={{
                    backgroundColor: isDarkTheme ? Colors.GRAY7 : Colors.GRAY6,
                  }}
                  transition={{ duration: 0.2 }}
                  onClick={checkRequestAccess(viewProfile)}
                >
                  <div className="AlignedRow" style={{ gap: 7 }}>
                    <div>
                      <ProfilePic
                        user={accountData}
                        size={40}
                        forcePointer
                        blueGradientBackground
                      />
                    </div>
                    <div className="ColumnNormal" style={{ gap: 4 }}>
                      <span className="bodyMedium" style={theme.PrimaryText}>
                        {accountData.fullName || "No Name"}
                      </span>
                      <span className="smallBodySubtext">
                        @{accountData.username}
                      </span>
                    </div>
                  </div>
                  <Icon
                    icon="ion:chevron-forward"
                    height={18}
                    style={{ color: theme.PrimaryText.color }}
                  />
                </m.div>
                <HorizontalDivider altMargin={0} eventTheme={eventTheme} />
                {additionalAccountData.length > 0 ? (
                  <>
                    <div
                      className="ColumnNormal"
                      style={{ paddingBlock: 12, gap: 4 }}
                    >
                      <span
                        className="smallBodySubtext"
                        style={{ fontSize: 11, paddingInline: 10 }}
                      >
                        Switch Account
                      </span>
                      <div>
                        {allAccounts.map((account) =>
                          renderMiniProfileItem(account)
                        )}
                      </div>
                    </div>
                    <HorizontalDivider altMargin={0} eventTheme={eventTheme} />
                  </>
                ) : null}
                <div className="ColumnNormal">
                  {renderItem("Add Account", "ion:plus", addAccountOnPress)}
                  <HorizontalDivider altMargin={0} eventTheme={eventTheme} />
                  {renderItem(
                    `Switch to ${
                      accountData.inCreatorMode ? "Subscriber" : "Creator"
                    }`,
                    "mdi:swap-horizontal-variant",
                    checkRequestAccess(switchMode)
                  )}
                  <HorizontalDivider altMargin={0} eventTheme={eventTheme} />
                  {accountData.isAdmin ? (
                    <>
                      {/* The legacy /adminDashboard is retired — its last
                          screen (Stripe payments) is now the master admin
                          dashboard's Payments tab. Repointed rather than
                          removed: this dropdown is the only admin entry point
                          on this surface. */}
                      {renderItem(
                        "Admin Dashboard",
                        "mdi:security-lock-outline",
                        () => navigate("/admin/master-dashboard")
                      )}
                      <HorizontalDivider
                        altMargin={0}
                        eventTheme={eventTheme}
                      />
                    </>
                  ) : null}
                  <div style={{ position: "relative" }}>
                    {renderItem(
                      "Sign Out",
                      additionalAccountData.length > 0
                        ? "ion:chevron-forward"
                        : "ion:exit",
                      () =>
                        additionalAccountData.length > 0
                          ? setSignOutDropdownVisible(true)
                          : signOutAllAccounts(),
                      true
                    )}
                    {signOutDropdownVisible ? (
                      <div
                        ref={ref2}
                        key="signOutDropdown"
                        className="HeaderProfilePicDropdown"
                        style={{
                          top: 54,
                          left: -4,
                          paddingInline: 4,
                          backgroundColor: isDarkTheme
                            ? Colors.GRAY10
                            : Colors.WHITE,
                          border: `1px solid ${theme.DividerColor.borderColor}`,
                          gap: 10,
                        }}
                      >
                        <div
                          className="ColumnNormal"
                          style={{ paddingBlock: 7 }}
                        >
                          {allAccounts.map((account) =>
                            renderMiniProfileItem(account, true)
                          )}
                        </div>
                        <HorizontalDivider
                          altMargin={0}
                          eventTheme={eventTheme}
                        />
                        {renderItem(
                          "Sign out of all accounts",
                          "ion:exit",
                          signOutAllAccounts,
                          true
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </m.div>
            </LazyMotion>
          </AnimatePresence>
        ) : null}
      </div>
    </div>
  );
};

export default ProfileHeaderDropdown;
